import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import client, { setToken } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Handles the first-party Google OAuth handoff. The backend callback redirects
// here with a URL fragment: #gtoken=... (logged in), #gpend=...&email=...&name=...
// (needs an invitation code), or #gerror=1 (failure).
export default function AuthCallback() {
  const navigate = useNavigate();
  const { finishAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = (window.location.hash || '').replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const gtoken = params.get('gtoken');
    const gpend = params.get('gpend');
    const gerror = params.get('gerror');

    const clean = () => window.history.replaceState({}, document.title, window.location.pathname);

    (async () => {
      if (gerror) {
        clean();
        toast.error('Google sign-in failed. Please try again.');
        navigate('/login', { replace: true });
        return;
      }
      if (gpend) {
        // New user without an invite — stash the pending handoff and go redeem a code.
        sessionStorage.setItem('hibret_gpend', JSON.stringify({
          temp_token: gpend,
          email: params.get('email') || '',
          name: params.get('name') || '',
        }));
        clean();
        navigate('/signup?google=1', { replace: true });
        return;
      }
      if (gtoken) {
        try {
          setToken(gtoken);
          const me = await client.get('/auth/me');
          await finishAuth({ token: gtoken, user: me.data });
          clean();
          navigate('/dashboard', { replace: true });
          return;
        } catch (e) {
          setToken(null);
          toast.error('Could not complete sign-in. Please try again.');
          navigate('/login', { replace: true });
          return;
        }
      }
      navigate('/login', { replace: true });
    })();
  }, [navigate, finishAuth]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <Logo />
      <p className="text-sm text-muted-foreground">Signing you in…</p>
    </div>
  );
}
